import React from 'react';
import { motion } from 'framer-motion';
import { ClipboardListIcon, BedDoubleIcon, ShoppingCartIcon, UtensilsIcon, GiftIcon } from 'lucide-react';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/wfCwzazkJgFyupSqRqJKQJ/heylloyd-image-1771802598740_reset_cortisol.png';
export function ProductSection() {
  return <section className="bg-white py-16 md:py-24" aria-labelledby="product-heading" data-id="element-460">
      <div className="max-w-5xl mx-auto px-4" data-id="element-461">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-14" data-id="element-462">
          <p className="font-sans text-terracotta text-xs font-700 tracking-[0.2em] uppercase mb-4" data-id="element-463">
            What You Get Today
          </p>
          <h2 id="product-heading" className="font-serif text-forest text-3xl md:text-4xl font-700 leading-tight" data-id="element-464">
            The Cortisol Reset Kitchen: Your Complete 21-Day Tactical Manual
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start" data-id="element-465">
          {/* Product Image */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="flex justify-center md:sticky md:top-24" data-id="element-466">
            <div className="relative" data-id="element-467">
              <div className="absolute inset-0 bg-sage/20 rounded-3xl blur-2xl transform scale-90 translate-y-6" aria-hidden="true" data-id="element-468" />
              <img src={PRODUCT_IMAGE} alt="The Cortisol Reset Kitchen digital guide" className="relative w-full max-w-xs md:max-w-sm rounded-2xl shadow-2xl" data-id="element-469" />
            </div>
          </motion.div>

          {/* Feature Boxes */}
          <div className="space-y-5" data-id="element-470">
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="flex gap-4 bg-cream rounded-2xl p-5 border border-sage/20" data-id="element-471">
              <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-472">
                <ClipboardListIcon className="w-6 h-6 text-forest" aria-hidden="true" data-id="element-473" />
              </div>
              <div data-id="element-474">
                <h3 className="font-serif text-forest text-lg font-700 mb-1" data-id="element-475">
                  The 21-Day Thermal Window Flush
                </h3>
                <p className="font-sans text-stone-600 text-sm leading-relaxed" data-id="element-476">
                  A day-by-day meal timing guide that works with your hormones,
                  not against them. No calorie counting. No deprivation.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex gap-4 bg-cream rounded-2xl p-5 border border-sage/20" data-id="element-477">
              <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-478">
                <BedDoubleIcon className="w-6 h-6 text-forest" aria-hidden="true" data-id="element-479" />
              </div>
              <div data-id="element-480">
                <h3 className="font-serif text-forest text-lg font-700 mb-1" data-id="element-481">
                  The Sleep-to-Slim Connection
                </h3>
                <p className="font-sans text-stone-600 text-sm leading-relaxed" data-id="element-482">
                  The exact evening protocol to stop 3am cortisol spikes — and
                  wake up lighter.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="flex gap-4 bg-cream rounded-2xl p-5 border border-sage/20" data-id="element-483">
              <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-484">
                <ShoppingCartIcon className="w-6 h-6 text-forest" aria-hidden="true" data-id="element-485" />
              </div>
              <div data-id="element-486">
                <h3 className="font-serif text-forest text-lg font-700 mb-1" data-id="element-487">
                  The Adrenal Grocery List
                </h3>
                <p className="font-sans text-stone-600 text-sm leading-relaxed" data-id="element-488">
                  The 23 specific foods that lower cortisol at the cellular
                  level. Plus the 7 "healthy" foods secretly spiking your stress
                  hormones.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="flex gap-4 bg-cream rounded-2xl p-5 border border-sage/20" data-id="element-489">
              <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-490">
                <UtensilsIcon className="w-6 h-6 text-forest" aria-hidden="true" data-id="element-491" />
              </div>
              <div data-id="element-492">
                <h3 className="font-serif text-forest text-lg font-700 mb-1" data-id="element-493">
                  The Anti-Inflammatory Recipe Collection
                </h3>
                <p className="font-sans text-stone-600 text-sm leading-relaxed" data-id="element-494">
                  40+ recipes timed to your Thermal Windows. Mediterranean
                  salmon salad, cortisol-calming breakfasts, and more.
                </p>
              </div>
            </motion.div>

            {/* Bonus Box */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.5
          }} className="flex gap-4 rounded-2xl p-5 border-2 border-forest bg-forest/5" data-id="element-495">
              <div className="w-12 h-12 bg-terracotta/15 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-496">
                <GiftIcon className="w-6 h-6 text-terracotta" aria-hidden="true" data-id="element-497" />
              </div>
              <div data-id="element-498">
                <div className="flex items-center gap-2 mb-1" data-id="element-499">
                  <span className="font-sans text-xs font-700 tracking-widest text-terracotta uppercase" data-id="element-500">
                    Bonus
                  </span>
                  <span className="font-sans text-xs text-stone-500 bg-terracotta/10 px-2 py-0.5 rounded-full" data-id="element-501">
                    $17 value — FREE
                  </span>
                </div>
                <h3 className="font-serif text-forest text-lg font-700 mb-1" data-id="element-502">
                  The "Cortisol SOS" Guide
                </h3>
                <p className="font-sans text-stone-600 text-sm leading-relaxed" data-id="element-503">
                  What to do on high-stress days to prevent fat storage. Your
                  emergency protocol when life gets overwhelming.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}